import { ART_PAL, ball, body, edge, fade, gloss, mix, n, rng, shade, shadowInk, tint, vgrad, warm, type ArtPal, type ArtTheme } from './covers/kit';
import { rasterArt, rasterBg } from './raster';

/*
 * Dragon Gate (鲤鱼跃龙门) game art in the "premium casual" style of the covers: the carp, the dragon it becomes,
 * a rock, a stone ledge, the waterfall and the Dragon Gate itself — a Chinese paifang (牌坊): three bays on four red
 * pillars, tiled roofs with upturned eaves, gold beams and a blank gold plaque. No text, no characters (captions
 * come from i18n only); no clocks, no green headwear, no white-and-black mourning pairs (docs/art-prompts.md).
 * Every builder takes the palette and a defs-id prefix; placeholders until the final graphics.
 */
export type CarpArtName = 'carp' | 'dragon' | 'rock' | 'ledge' | 'gate' | 'fall';

/* ---------------- the Dragon Gate: paifang ---------------- */

/** Local box of `paifang()`: 300 × 170, pillars stand on y = 170. */
export const PAIFANG_W = 300;
export const PAIFANG_H = 170;

/** Paifang defs (gradients); ids `${p}pr` pillar, `${p}rt` roof tiles, `${p}bm` beam, `${p}pl` plaque, `${p}st` plinth. */
export function paifangDefs(P: ArtPal, p: string): string {
  const pillar = P.red;
  const roof = warm(P);
  const stone = P.dark ? mix(P.slate, P.ink, 0.35) : tint(P.slate, 0.6);
  return (
    body(`${p}pr`, pillar, 0.3, 0.35, 1, 0.2) +
    vgrad(`${p}rt`, tint(roof, 0.25), roof, shade(roof, 0.25)) +
    body(`${p}bm`, shade(pillar, 0.08), 0.25, 0.3, 0.5, 1) +
    body(`${p}pl`, roof, 0.45, 0.3, 0.5, 1) +
    body(`${p}st`, stone, 0.3, 0.3, 0.5, 1)
  );
}

/** One tiled roof with upturned eaves: centre x, eave line y, half-width w, height h. */
function roof(P: ArtPal, p: string, cx: number, y: number, w: number, h: number): string {
  const c = warm(P);
  const e = edge(c, P);
  const top = y - h;
  const rw = w * 0.72; // ridge half-width
  // body of the roof (tiles) and the sweeping eave with tips curling up
  const bodyPath = `M${n(cx - w * 0.86)} ${n(y)}Q${n(cx - w * 0.7)} ${n(top + h * 0.35)} ${n(cx - rw)} ${n(top)}L${n(cx + rw)} ${n(top)}Q${n(cx + w * 0.7)} ${n(top + h * 0.35)} ${n(cx + w * 0.86)} ${n(y)}Z`;
  const eave = `M${n(cx - w - 8)} ${n(y - 10)}Q${n(cx - w + 2)} ${n(y + 2)} ${n(cx - w * 0.8)} ${n(y + 1)}L${n(cx + w * 0.8)} ${n(y + 1)}Q${n(cx + w - 2)} ${n(y + 2)} ${n(cx + w + 8)} ${n(y - 10)}Q${n(cx + w + 2)} ${n(y + 9)} ${n(cx + w * 0.8)} ${n(y + 8)}L${n(cx - w * 0.8)} ${n(y + 8)}Q${n(cx - w - 2)} ${n(y + 9)} ${n(cx - w - 8)} ${n(y - 10)}Z`;
  let tiles = '';
  const k = Math.max(5, Math.round(w / 9));
  for (let i = 1; i < k * 2; i++) {
    const x = cx - w * 0.8 + (i * w * 1.6) / (k * 2);
    const x0 = cx + (x - cx) * (rw / (w * 0.8));
    tiles += `M${n(x0)} ${n(top + 2)}L${n(x)} ${n(y)}`;
  }
  // ridge with curled ends
  const ridge =
    `<rect x="${n(cx - rw - 2)}" y="${n(top - 5)}" width="${n(rw * 2 + 4)}" height="7" rx="3" fill="${shade(c, 0.12)}" stroke="${e}" stroke-width="1.5"/>` +
    `<path d="M${n(cx - rw - 2)} ${n(top - 2)}Q${n(cx - rw - 10)} ${n(top - 4)} ${n(cx - rw - 9)} ${n(top - 13)}Q${n(cx - rw - 3)} ${n(top - 11)} ${n(cx - rw + 2)} ${n(top - 5)}Z" fill="${c}" stroke="${e}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<path d="M${n(cx + rw + 2)} ${n(top - 2)}Q${n(cx + rw + 10)} ${n(top - 4)} ${n(cx + rw + 9)} ${n(top - 13)}Q${n(cx + rw + 3)} ${n(top - 11)} ${n(cx + rw - 2)} ${n(top - 5)}Z" fill="${c}" stroke="${e}" stroke-width="1.5" stroke-linejoin="round"/>`;
  return (
    `<path d="${bodyPath}" fill="url(#${p}rt)" stroke="${e}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="${tiles}" stroke="${shade(c, 0.3)}" stroke-opacity=".45" stroke-width="1.5"/>` +
    `<path d="${eave}" fill="${shade(c, 0.1)}" stroke="${e}" stroke-width="2" stroke-linejoin="round"/>` +
    ridge
  );
}

/**
 * The paifang in its local 300 × 170 box (defs from `paifangDefs` must be in the same SVG): four red pillars on stone
 * plinths, three bays (the middle one taller), gold beams, a blank gold plaque and a pearl on the middle ridge.
 */
export function paifang(P: ArtPal, p: string): string {
  const red = P.red;
  const er = edge(red, P);
  const gold = warm(P);
  const eg = edge(gold, P);
  const cols = [34, 104, 196, 266];
  let pillars = '';
  for (const x of cols) {
    const inner = x === 104 || x === 196;
    const top = inner ? 58 : 84;
    pillars +=
      `<rect x="${x - 8}" y="${top}" width="16" height="${160 - top}" rx="3" fill="url(#${p}pr)" stroke="${er}" stroke-width="2"/>` +
      `<rect x="${x - 13}" y="152" width="26" height="18" rx="3" fill="url(#${p}st)" stroke="${edge(P.slate, P)}" stroke-width="1.5"/>` +
      `<rect x="${x - 10}" y="${top + 6}" width="20" height="5" rx="2" fill="${gold}" stroke="${eg}" stroke-width="1"/>` +
      gloss(x - 3, top + 40, 2, 16, 0, 0.3);
  }
  const beam = (x0: number, x1: number, y: number, h: number) =>
    `<rect x="${x0}" y="${y}" width="${x1 - x0}" height="${h}" rx="2" fill="url(#${p}bm)" stroke="${er}" stroke-width="1.5"/>` +
    `<path d="M${x0 + 4} ${y + h / 2}H${x1 - 4}" stroke="${gold}" stroke-width="2" stroke-dasharray="10 6" stroke-linecap="round"/>`;
  const beams =
    beam(92, 208, 64, 12) +
    beam(92, 208, 98, 10) +
    beam(22, 116, 90, 11) +
    beam(184, 278, 90, 11) +
    beam(22, 116, 112, 8) +
    beam(184, 278, 112, 8);
  // blank plaque between the middle beams (no characters — captions come from i18n)
  const plaque =
    `<rect x="128" y="75" width="44" height="24" rx="3" fill="url(#${p}pl)" stroke="${eg}" stroke-width="2"/>` +
    `<rect x="133" y="79" width="34" height="16" rx="2" fill="none" stroke="${shade(gold, 0.25)}" stroke-opacity=".6" stroke-width="1.5"/>`;
  // flaming pearl on top of the middle ridge
  const pearl =
    `<path d="M150 6Q143 14 146 20Q150 16 150 22Q150 16 154 20Q157 14 150 6Z" fill="${red}" fill-opacity=".85"/>` +
    `<circle cx="150" cy="20" r="6" fill="${tint(gold, 0.25)}" stroke="${eg}" stroke-width="1.5"/>` +
    gloss(148, 18, 2, 1.5, -30, 0.7);
  return (
    pillars +
    beams +
    roof(P, p, 69, 88, 46, 18) +
    roof(P, p, 231, 88, 46, 18) +
    plaque +
    roof(P, p, 150, 60, 66, 26) +
    pearl
  );
}

/* ---------------- the carp ---------------- */

/** Defs of the carp: `${p}fb` body, `${p}fn` fins. */
export function carpFishDefs(P: ArtPal, p: string): string {
  const fish = P.orange;
  return body(`${p}fb`, fish, 0.4, 0.3, 0.5, 1) + ball(`${p}fn`, tint(fish, 0.15), 0.35, 0.3);
}

/** The carp lying along +x (nose at x ≈ 72, tail at x ≈ −86), centred on 0,0 — rotate / scale it in place. */
export function carpFish(P: ArtPal, p: string): string {
  const fish = P.orange;
  const e = edge(fish, P);
  return (
    `<path d="M-52 0Q-70 -22 -86 -20Q-74 0 -86 20Q-70 22 -52 0Z" fill="url(#${p}fn)" stroke="${e}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M-6 -22Q2 -38 18 -34Q14 -26 10 -20Z" fill="url(#${p}fn)" stroke="${e}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M-56 0Q-30 -28 14 -26Q48 -22 58 0Q48 22 14 26Q-30 28 -56 0Z" fill="url(#${p}fb)" stroke="${e}" stroke-width="2.5"/>` +
    `<path d="M-6 16Q4 30 16 30Q12 22 12 16Z" fill="url(#${p}fn)" stroke="${e}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M-28 -8Q-18 -2 -28 6M-14 -12Q-2 -2 -14 10M0 -14Q12 -2 0 12" fill="none" stroke="${shade(fish, 0.25)}" stroke-opacity=".55" stroke-width="2" stroke-linecap="round"/>` +
    `<path d="M34 -18Q28 0 34 18" fill="none" stroke="${shade(fish, 0.3)}" stroke-opacity=".6" stroke-width="2"/>` +
    `<circle cx="44" cy="-6" r="5.5" fill="#FFFFFF"/><circle cx="45" cy="-6" r="3" fill="${P.dark ? P.bg : P.ink}"/>` +
    `<path d="M58 4Q70 8 72 18" fill="none" stroke="${e}" stroke-width="2" stroke-linecap="round"/>` +
    gloss(-4, -14, 22, 6, -6, 0.45)
  );
}

/* ---------------- builders of the game pieces (own boxes) ---------------- */

interface Piece {
  w: number;
  h: number;
  /** extra attributes of the root <svg> */
  attrs?: string;
  b(P: ArtPal, p: string): string;
}

/** Carp leaping up (nose up), 80 × 120. */
function carpPiece(P: ArtPal, p: string): string {
  return (
    `<defs>${carpFishDefs(P, p)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.45 : 0.16)}</defs>` +
    `<ellipse cx="40" cy="112" rx="24" ry="5" fill="url(#${p}sh)"/>` +
    `<g transform="translate(40 58) rotate(-80) scale(.62)">${carpFish(P, p)}</g>`
  );
}

/** The golden dragon the carp becomes at the gate: an S-shaped body rising up, red mane, horns, whiskers. 120 × 120. */
function dragonPiece(P: ArtPal, p: string): string {
  const gold = warm(P);
  const eg = edge(gold, P);
  const red = P.red;
  const spine = 'M26 112C6 92 22 74 44 76S84 70 76 50S52 30 70 18';
  const head =
    `<g transform="translate(74 18) rotate(-18)">` +
    // horns
    `<path d="M-6 -8Q-12 -22 -4 -30M4 -8Q2 -22 12 -28" fill="none" stroke="${eg}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="M-6 -8Q-12 -22 -4 -30M4 -8Q2 -22 12 -28" fill="none" stroke="${tint(gold, 0.35)}" stroke-width="2" stroke-linecap="round"/>` +
    // mane
    `<path d="M-14 -4Q-24 2 -20 12Q-14 6 -10 10Q-12 0 -14 -4Z" fill="${red}" stroke="${edge(red, P)}" stroke-width="1.5" stroke-linejoin="round"/>` +
    // head + snout
    `<path d="M-14 -8Q0 -16 14 -8L26 -4Q30 2 26 6L12 10Q-2 14 -14 6Z" fill="url(#${p}db)" stroke="${eg}" stroke-width="2" stroke-linejoin="round"/>` +
    `<circle cx="6" cy="-4" r="3.5" fill="#FFFFFF"/><circle cx="7" cy="-4" r="2" fill="${P.dark ? P.bg : P.ink}"/>` +
    // whiskers
    `<path d="M24 4Q36 10 40 22M22 -2Q36 -6 42 -16" fill="none" stroke="${red}" stroke-width="2" stroke-linecap="round"/>` +
    `</g>`;
  return (
    `<defs>${body(`${p}db`, gold, 0.45, 0.35, 0.5, 1)}${fade(`${p}gw`, P.dark ? gold : '#FFFFFF', P.dark ? 0.4 : 0.8)}</defs>` +
    `<circle cx="60" cy="60" r="58" fill="url(#${p}gw)"/>` +
    // body: outline, gold, lighter belly scales, red dorsal flames
    `<path d="${spine}" fill="none" stroke="${eg}" stroke-width="18" stroke-linecap="round"/>` +
    `<path d="${spine}" fill="none" stroke="url(#${p}db)" stroke-width="14" stroke-linecap="round"/>` +
    `<path d="${spine}" fill="none" stroke="${tint(gold, 0.5)}" stroke-width="4" stroke-linecap="round" stroke-dasharray="3 5"/>` +
    `<path d="M18 94L10 88L20 86M40 70L36 62L46 64M74 58L82 52L80 62M64 30L58 24L68 24" fill="none" stroke="${red}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` +
    // claws
    `<path d="M40 82Q36 94 30 96M68 64Q78 70 84 68" fill="none" stroke="${eg}" stroke-width="3" stroke-linecap="round"/>` +
    // tail fin
    `<path d="M26 112Q14 116 8 108Q18 108 22 104Z" fill="${red}" stroke="${edge(red, P)}" stroke-width="1.5" stroke-linejoin="round"/>` +
    head
  );
}

/** A wet river rock, 80 × 56. */
function rockPiece(P: ArtPal, p: string): string {
  const c = P.dark ? mix(P.slate, P.ink, 0.28) : mix(P.slate, '#FFFFFF', 0.35);
  return (
    `<defs>${ball(`${p}rk`, c, 0.35, 0.4)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.2)}</defs>` +
    `<ellipse cx="40" cy="50" rx="32" ry="5" fill="url(#${p}sh)"/>` +
    `<path d="M10 44Q6 26 20 16Q34 6 50 10Q70 14 72 32Q74 46 60 48L22 50Q12 50 10 44Z" fill="url(#${p}rk)" stroke="${edge(c, P)}" stroke-width="2.5" stroke-linejoin="round"/>` +
    `<path d="M30 20Q38 26 34 36M52 18Q58 28 54 38" fill="none" stroke="${shade(c, 0.3)}" stroke-opacity=".5" stroke-width="2" stroke-linecap="round"/>` +
    gloss(28, 20, 9, 4, -25, 0.5)
  );
}

/** A stone ledge with water spilling over its front edge, 120 × 44 (stretched to the cell width). */
function ledgePiece(P: ArtPal, p: string): string {
  const stone = P.dark ? mix(P.slate, P.ink, 0.18) : mix(P.slate, '#FFFFFF', 0.55);
  const water = P.dark ? mix(P.sky, P.teal, 0.3) : tint(P.sky, 0.35);
  return (
    `<defs>${body(`${p}ls`, stone, 0.3, 0.35, 0.3, 1)}${vgrad(`${p}lw`, tint(water, 0.4), water, shade(water, 0.1))}</defs>` +
    `<path d="M4 16Q4 8 14 8H106Q116 8 116 16V30Q116 36 108 36H12Q4 36 4 30Z" fill="url(#${p}ls)" stroke="${edge(stone, P)}" stroke-width="2"/>` +
    `<path d="M8 14Q8 10 14 10H106Q112 10 112 14" fill="none" stroke="#FFFFFF" stroke-opacity=".35" stroke-width="2" stroke-linecap="round"/>` +
    `<path d="M18 34Q20 40 18 44H102Q100 40 102 34Z" fill="url(#${p}lw)" fill-opacity=".85"/>` +
    `<path d="M30 36V43M52 36V44M74 36V43M92 36V44" stroke="#FFFFFF" stroke-opacity=".6" stroke-width="2" stroke-linecap="round"/>`
  );
}

/** The Dragon Gate (paifang) over a glow of light, 300 × 180. */
function gatePiece(P: ArtPal, p: string): string {
  return (
    `<defs>${paifangDefs(P, p)}${fade(`${p}gl`, P.dark ? P.gold : '#FFFFFF', P.dark ? 0.35 : 0.75)}</defs>` +
    `<ellipse cx="150" cy="80" rx="150" ry="80" fill="url(#${p}gl)"/>` +
    `<g transform="translate(0 6)">${paifang(P, p)}</g>`
  );
}

/** The waterfall column (stretched to the board, preserveAspectRatio none): water, foam streaks, spray. 200 × 400. */
function fallPiece(P: ArtPal, p: string): string {
  const water = P.dark ? mix(P.sky, P.teal, 0.35) : tint(P.sky, 0.3);
  const r = rng(88);
  let streaks = '';
  for (let i = 0; i < 14; i++) {
    const x = 20 + i * 12 + (r() - 0.5) * 6;
    const y0 = r() * 300;
    streaks += `<path d="M${n(x)} ${n(y0)}V${n(y0 + 60 + r() * 120)}" stroke="#FFFFFF" stroke-opacity="${n(0.12 + r() * 0.2)}" stroke-width="${n(2 + r() * 2)}" stroke-linecap="round" vector-effect="non-scaling-stroke"/>`;
  }
  return (
    `<defs>${vgrad(`${p}wf`, tint(water, P.dark ? 0.1 : 0.45), water, shade(water, P.dark ? 0.35 : 0.05))}</defs>` +
    `<rect x="12" width="176" height="400" fill="url(#${p}wf)" fill-opacity="${P.dark ? 0.55 : 0.6}"/>` +
    streaks +
    `<ellipse cx="100" cy="398" rx="100" ry="14" fill="#FFFFFF" fill-opacity=".35"/>`
  );
}

const PIECES: Record<CarpArtName, Piece> = {
  carp: { w: 80, h: 120, b: carpPiece },
  dragon: { w: 120, h: 120, b: dragonPiece },
  rock: { w: 80, h: 56, b: rockPiece },
  ledge: { w: 120, h: 44, attrs: ' preserveAspectRatio="none"', b: ledgePiece },
  gate: { w: 300, h: 180, b: gatePiece },
  fall: { w: 200, h: 400, attrs: ' preserveAspectRatio="none"', b: fallPiece },
};

export function carpArtSize(name: CarpArtName): { w: number; h: number } {
  const b = PIECES[name];
  return { w: b.w, h: b.h };
}

/** Standalone SVG of a piece (ids prefixed per piece + theme). */
export function carpArtSvg(name: CarpArtName, theme: ArtTheme, prefix = `k${name}${theme[0]}`): string {
  const b = PIECES[name];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${b.w} ${b.h}" width="${b.w}" height="${b.h}"${b.attrs ?? ''}>${b.b(ART_PAL[theme], prefix)}</svg>`;
}

const uris = new Map<string, string>();

/**
 * URL of a piece: the raster (`carp/<name>`, the waterfall `carp/fall-<theme>`) once `pnpm art:process` has produced
 * it, else the data URI of the vector piece (cached per theme).
 */
export function carpArtUri(name: CarpArtName, theme: ArtTheme): string {
  const raster = name === 'fall' ? rasterBg('carp', 'fall', theme) : rasterArt('carp', name);
  if (raster) return raster;
  const key = name + theme;
  let u = uris.get(key);
  if (!u) {
    u = 'data:image/svg+xml;utf8,' + encodeURIComponent(carpArtSvg(name, theme));
    uris.set(key, u);
  }
  return u;
}

/**
 * The ledge under a cell: a shown rock lies on the cracked ledge (raster `carp/ledge-broken`, Codex task 3) — the same
 * on every rock cell of the revealed layout, the one the carp fell from included (no "so close" marks). Without that
 * raster every cell keeps the plain ledge.
 */
export function carpLedgeUri(rock: boolean, theme: ArtTheme): string {
  return (rock && rasterArt('carp', 'ledge-broken')) || carpArtUri('ledge', theme);
}

export const CARP_ART_NAMES = Object.keys(PIECES) as CarpArtName[];
